import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { RootStore } from "app/rootStore";
import { UserNotification } from "shared/interfaces/notifications";
import {
    deleteAllUserNotifications,
    deleteUserNotification,
    getNotReadNotificationsCount,
    getUserNotifications,
    markAllUserNotificationsAsRead,
} from "shared/api/notificationService/appNotifications";

export class AppNotificationsStore {
    private rootStore: RootStore;
    @observable public notifications: Array<UserNotification> = [];
    @observable public notReadCount = 0;

    private fetchNotificationsIntervalId?: number;
    private fetchNotReadCountIntervalId?: number;

    constructor(rootStore: RootStore) {
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action.bound
    public async markAllAsRead(): Promise<void> {
        await markAllUserNotificationsAsRead();

        runInAction(() => {
            this.notReadCount = 0;
            this.notifications = this.notifications.map(notification => ({ ...notification, isRead: true }));
        });
    }

    @action.bound
    public async deleteNotification(index: number): Promise<void> {
        const notification = this.notifications[index];

        if (notification) {
            await deleteUserNotification(notification.appNotificationId);

            runInAction(() => {
                this.notifications.splice(index, 1);
                this.notifications = [...this.notifications];
            });
        }
    }

    @action.bound
    public async deleteAllNotifications(): Promise<void> {
        await deleteAllUserNotifications();

        runInAction(() => {
            this.notifications = [];
        });
    }

    startFetchNotifications = async (updateIntervalMs: number): Promise<void> => {
        if (!this.fetchNotificationsIntervalId) {
            // eslint-disable-next-line @typescript-eslint/no-implied-eval
            this.fetchNotificationsIntervalId = window.setInterval(this.fetchNotifications, updateIntervalMs);

            await this.fetchNotifications();
        }
    };

    startFetchNotReadCount = async (updateIntervalMs: number): Promise<void> => {
        if (this.fetchNotReadCountIntervalId) return;
        // eslint-disable-next-line @typescript-eslint/no-implied-eval
        this.fetchNotReadCountIntervalId = window.setInterval(this.fetchNotReadCount, updateIntervalMs);

        await this.fetchNotReadCount();
    };

    stopFetchNotReadCount = (): void => {
        if (this.fetchNotReadCountIntervalId) {
            clearInterval(this.fetchNotReadCountIntervalId);

            this.fetchNotReadCountIntervalId = undefined;
        }
    };

    stopFetchNotifications = (): void => {
        if (this.fetchNotificationsIntervalId) {
            clearInterval(this.fetchNotificationsIntervalId);

            this.fetchNotificationsIntervalId = undefined;
        }
    };

    @action.bound
    public clearStore(): void {
        this.notifications = [];
        this.notReadCount = 0;
    }

    @action.bound
    private setNotifications(notifications: UserNotification[]) {
        this.notifications = notifications;
    }

    @action.bound
    private setNotReadCount(count: number) {
        this.notReadCount = count;
    }

    private fetchNotReadCount = async () => {
        this.setNotReadCount(await getNotReadNotificationsCount());
    };

    private fetchNotifications = async () => {
        this.setNotifications(await getUserNotifications());
    };

    @computed
    get hasNotifications(): boolean {
        return Boolean(this.notifications.length);
    }
}
